import * as React from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Skeleton from "@mui/material/Skeleton";
import { useState, useContext, useEffect } from "react";
import { CommonContext } from "../../contexts/CommonContext";
import Grid from "@mui/material/Unstable_Grid2";
import { useNavigate } from "react-router-dom";
import ComponentLoader from "../../components/ComponentLoader";
import { getPilotProducts, getNearestStoreHaversine } from "../../services/api";
import { savePilotStore } from "../../utils/pilotUtils";
import { Helmet } from "react-helmet";
import NavHeader from "../../components/NavHeader";
import ImageSlider from "../../components/ImageSlider";
import { motion } from "framer-motion";
import PilotCartFAB from "../../components/PilotCartFAB";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";

const USE_DEV_LOCATION = process.env.NODE_ENV === "development";

const DEV_LOCATION = {
  lat: 17.4918883,
  lng: 78.3327012,
};

const getLocation = () => {
  return new Promise((resolve, reject) => {
    if (USE_DEV_LOCATION) {
      resolve(DEV_LOCATION);
      return;
    }
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not supported by your browser"));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) => {
        if (err.code === 1) reject(new Error("Location permission denied"));
        else if (err.code === 2) reject(new Error("Location unavailable"));
        else reject(new Error(err.message || "Failed to get location"));
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  });
};

function Products() {
  const navigate = useNavigate();
  const { isDesktop } = useContext(CommonContext);

  const [loading, setLoading] = useState(true);
  const [products, setProducts] = useState([]);
  const [productsByCategory, setProductsByCategory] = useState({});
  const [error, setError] = useState(null);
  const [storeDetails, setStoreDetails] = useState(null);

  const checkInStoreAndFetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const { lat, lng } = await getLocation();
      const result = await getNearestStoreHaversine(lat, lng);
      if (result.success && result.store) {
        await savePilotStore(result.store);
        setStoreDetails(result.store);
        await fetchProducts();
      } else {
        setError("Please visit our store and scan the QR code to order");
        setLoading(false);
      }
    } catch (err) {
      const msg = err?.message || err?.error || "Error verifying store location";
      setError(msg);
      setLoading(false);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getPilotProducts();
      console.log("API Response:", response);
      
      // Handle both success property and direct data array
      if (response && (response.success || response.data || Array.isArray(response))) {
        const productsList = response.data || response || [];
        setProducts(productsList);
        
        // Group products by category
        const grouped = {};
        productsList.forEach((product) => {
          const category = product.category || "Uncategorized";
          if (!grouped[category]) {
            grouped[category] = [];
          }
          grouped[category].push(product);
        });
        
        // Sort categories alphabetically
        const sortedCategories = Object.keys(grouped).sort();
        const sortedGrouped = {};
        sortedCategories.forEach((category) => {
          sortedGrouped[category] = grouped[category];
        });
        
        setProductsByCategory(sortedGrouped);
      } else {
        setError(response?.message || "Failed to fetch products");
      }
    } catch (err) {
      console.error("Error fetching products:", err);
      setError(err?.message || err?.error || "Error loading products. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkInStoreAndFetchProducts();
  }, []);

  return (
    <Box
      sx={{
        padding: isDesktop ? "4vw 10vw" : "4vw",
        marginTop: "7vh",
        minHeight: "100vh",
        marginBottom: "7vh",
      }}
    >
      {loading ? (
        <ComponentLoader />
      ) : error ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "40px",
            textAlign: "center",
          }}
        >
          <Box sx={{ color: "#a4243d", fontSize: "18px", mb: 2 }}>
            {error}
          </Box>
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Button
              variant="contained"
              onClick={checkInStoreAndFetchProducts}
              sx={{
                background: "linear-gradient(135deg, #a4243d 0%, #ff6b6b 100%)",
                color: "white",
                fontWeight: "700",
                padding: "12px 32px",
                borderRadius: "12px",
                textTransform: "none",
                fontFamily: "RobotoSlab",
                boxShadow: "0 4px 15px rgba(164, 36, 61, 0.3)",
                "&:hover": {
                  background: "linear-gradient(135deg, #8a1e33 0%, #e55555 100%)",
                  boxShadow: "0 6px 20px rgba(164, 36, 61, 0.4)",
                },
              }}
            >
              Retry
            </Button>
          </motion.div>
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            background: "rgba(255, 246, 233, 1)",
          }}
        >
          <Helmet>
            <title>Pilot Products</title>
            <meta name="description" content="Browse our collection of products" />
          </Helmet>
          <NavHeader />
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <Box
              sx={{
                background: "linear-gradient(135deg, #a4243d 0%, #d63384 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                fontSize: isDesktop ? "48px" : "36px",
                ml: "4vw",
                mb: 3,
                fontWeight: "700",
                fontFamily: "Foregen",
                textShadow: "0 2px 10px rgba(164, 36, 61, 0.2)",
                letterSpacing: "2px",
              }}
            >
              Country Chicken Co
            </Box>
          </motion.div>
          {storeDetails && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.2 }}
            >
              <Paper
                elevation={0}
                sx={{
                  padding: "16px 20px",
                  margin: "0 4vw 24px 4vw",
                  borderRadius: "12px",
                  background: "linear-gradient(135deg, rgba(164, 36, 61, 0.08) 0%, rgba(255, 107, 107, 0.08) 100%)",
                  border: "1px solid rgba(164, 36, 61, 0.2)",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2 }}>
                  <LocationOnIcon
                    sx={{
                      color: "#a4243d",
                      fontSize: 28,
                      mt: 0.3,
                    }}
                  />
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: "#6c757d",
                        fontFamily: "RobotoSlab",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                      }}
                    >
                      Ordering from
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: "20px",
                        fontWeight: "700",
                        color: "#2d3436",
                        fontFamily: "RobotoSlabBold",
                      }}
                    >
                      {storeDetails.storeName}
                    </Typography>
                    {storeDetails.address && (
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#6c757d",
                          mt: 0.5,
                          fontFamily: "RobotoSlab",
                        }}
                      >
                        {storeDetails.address}
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Paper>
            </motion.div>
          )}
          {Object.keys(productsByCategory).length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "60px 40px",
                  textAlign: "center",
                  background: "white",
                  borderRadius: "20px",
                  boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                  margin: "20px",
                }}
              >
                <Box
                  sx={{
                    fontSize: "48px",
                    marginBottom: "16px",
                    opacity: 0.3,
                  }}
                >
                  🛒
                </Box>
                <Box
                  sx={{
                    color: "#6c757d",
                    fontSize: "18px",
                    fontWeight: "600",
                    fontFamily: "RobotoSlab",
                  }}
                >
                  No products available at the moment.
                </Box>
              </Box>
            </motion.div>
          ) : (
            <Box>
              {Object.entries(productsByCategory).map(([category, categoryProducts], categoryIndex) => (
                <motion.div
                  key={category}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: categoryIndex * 0.1 }}
                >
                  <Box sx={{ marginBottom: "60px" }}>
                    <Box
                      sx={{
                        background: "linear-gradient(135deg, #a4243d 0%, #ff6b6b 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                        backgroundClip: "text",
                        fontSize: isDesktop ? "36px" : "28px",
                        ml: "4vw",
                        mb: 4,
                        mt: categoryIndex === 0 ? 0 : 6,
                        fontWeight: "700",
                        fontFamily: "Foregen",
                        textTransform: "uppercase",
                        letterSpacing: "3px",
                        position: "relative",
                        display: "inline-block",
                        "&::after": {
                          content: '""',
                          position: "absolute",
                          bottom: "-8px",
                          left: 0,
                          width: "60px",
                          height: "4px",
                          background: "linear-gradient(90deg, #a4243d 0%, #ff6b6b 100%)",
                          borderRadius: "2px",
                          boxShadow: "0 2px 8px rgba(164, 36, 61, 0.3)",
                        },
                      }}
                    >
                      {category}
                    </Box>
                  <Grid container spacing={3} sx={{ px: { xs: 2, md: 0 } }}>
                    {categoryProducts.map((product, productIndex) => {
                      // Get the first variant as default
                      const firstVariant = product.variants && product.variants.length > 0 
                        ? product.variants[0] 
                        : null;
                      
                      return (
                        <Grid
                          xs={12}
                          sm={6}
                          md={4}
                          lg={3}
                          key={product.id}
                        >
                          <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ 
                              duration: 0.4, 
                              delay: (categoryIndex * 0.1) + (productIndex * 0.05),
                              ease: "easeOut"
                            }}
                            whileHover={{ y: -8, transition: { duration: 0.2 } }}
                          >
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: isDesktop ? "row" : "column",
                        background: "white",
                        borderRadius: "20px",
                        overflow: "hidden",
                        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
                        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        "&:hover": {
                          boxShadow: "0 12px 40px rgba(164, 36, 61, 0.15)",
                          transform: "translateY(-8px)",
                        },
                        cursor: "pointer",
                        height: "100%",
                      }}
                    >
                      <Box
                        sx={{
                          textAlign: "center",
                          position: "relative",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          width: isDesktop ? "40%" : "100%",
                          minHeight: isDesktop ? "250px" : "200px",
                          background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                          overflow: "hidden",
                          "&::before": {
                            content: '""',
                            position: "absolute",
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: "linear-gradient(135deg, rgba(164, 36, 61, 0.05) 0%, rgba(255, 107, 107, 0.05) 100%)",
                            zIndex: 1,
                          },
                        }}
                        onClick={() => navigate(`/pilot/products/${product.id}`)}
                      >
                        {product.images && product.images.length > 1 ? (
                          <Box sx={{ width: "100%", height: "100%", position: "relative", zIndex: 2 }}>
                            <ImageSlider imgUrls={product.images} />
                          </Box>
                        ) : (product.images && product.images.length === 1) || product.image ? (
                          <motion.img
                            src={product.images?.[0] || product.image}
                            alt={product.name}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                            whileHover={{ scale: 1.1 }}
                            transition={{ duration: 0.3 }}
                            onError={(e) => {
                              e.target.src =
                                "https://via.placeholder.com/300x200?text=No+Image";
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              width: "100%",
                              height: "100%",
                              background: "linear-gradient(135deg, #e9ecef 0%, #dee2e6 100%)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#999",
                              fontSize: "14px"
                            }}
                          >
                            No Image
                          </Box>
                        )}
                      </Box>
                      <Box
                        sx={{
                          padding: "24px",
                          textAlign: "left",
                          background: "white",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "space-between",
                          flex: 1,
                          width: isDesktop ? "60%" : "100%",
                        }}
                      >
                        <Box>
                          <Box
                            sx={{
                              textAlign: "left",
                              marginBottom: "8px",
                              fontWeight: "700",
                              fontSize: isDesktop ? "22px" : "20px",
                              cursor: "pointer",
                              fontFamily: "Foregen",
                              textTransform: "uppercase",
                              background: "linear-gradient(135deg, #2d3436 0%, #636e72 100%)",
                              WebkitBackgroundClip: "text",
                              WebkitTextFillColor: "transparent",
                              backgroundClip: "text",
                              letterSpacing: "1px",
                              transition: "all 0.3s ease",
                              "&:hover": {
                                background: "linear-gradient(135deg, #a4243d 0%, #ff6b6b 100%)",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                                backgroundClip: "text",
                              },
                            }}
                          >
                            {product.name}
                          </Box>
                          {product.metadata?.style && (
                            <Box
                              sx={{
                                textAlign: "left",
                                fontSize: "13px",
                                fontWeight: "600",
                                color: "#6c757d",
                                display: "flex",
                                flexDirection: "row",
                                fontFamily: "RobotoSlab",
                                justifyContent: "space-between",
                                marginTop: "4px",
                                padding: "4px 8px",
                                background: "linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)",
                                borderRadius: "8px",
                                width: "fit-content",
                              }}
                            >
                              {product.metadata.style}
                              {product.metadata.aka && (
                                <> &nbsp; | &nbsp; Aka - {product.metadata.aka}</>
                              )}
                            </Box>
                          )}
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1.5,marginBottom: "20px" }}>
                            {product.metadata?.age && (
                              <Box
                                sx={{
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  fontFamily: "RobotoSlab",
                                  color: "#495057",
                                  padding: "4px 10px",
                                  background: "linear-gradient(135deg, #fff3cd 0%, #ffe69c 100%)",
                                  borderRadius: "12px",
                                  border: "1px solid #ffc107",
                                }}
                              >
                                Age: {product.metadata.age}
                              </Box>
                            )}
                            {firstVariant?.weight ? (
                              <Box
                                sx={{
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  color: "#495057",
                                  fontFamily: "RobotoSlab",
                                  padding: "4px 10px",
                                  background: "linear-gradient(135deg, #d1ecf1 0%, #bee5eb 100%)",
                                  borderRadius: "12px",
                                  border: "1px solid #17a2b8",
                                }}
                              >
                                {firstVariant.weight}
                              </Box>
                            ) : (product.netWeight || product.grossWeight) ? (
                              <Box
                                sx={{
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  color: "#495057",
                                  fontFamily: "RobotoSlab",
                                  padding: "4px 10px",
                                  background: "linear-gradient(135deg, #d1ecf1 0%, #bee5eb 100%)",
                                  borderRadius: "12px",
                                  border: "1px solid #17a2b8",
                                }}
                              >
                                {product.netWeight || product.grossWeight}
                              </Box>
                            ) : null}
                            {product.variants && product.variants.length > 1 && (
                              <Box
                                sx={{
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  color: "#495057",
                                  fontFamily: "RobotoSlab",
                                  padding: "4px 10px",
                                  background: "linear-gradient(135deg, #f8d7da 0%, #f5c6cb 100%)",
                                  borderRadius: "12px",
                                  border: "1px solid #dc3545",
                                }}
                              >
                                {product.variants.length} sizes
                              </Box>
                            )}
                          </Box>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 2,
                            marginTop: "auto",
                            paddingTop: "20px",
                            borderTop: "1px solid #e9ecef",
                            width: "88%",
                            minWidth: 0, // Prevents overflow
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              flex: 1,
                              minWidth: 0, // Allows text to shrink
                              overflow: "hidden",
                            }}
                          >
                            {(firstVariant?.price || product.livePrice || product.price) && (
                              <motion.div
                                initial={{ opacity: 0, scale: 0.8 }}
                                animate={{ opacity: 1, scale: 1 }}
                                transition={{ delay: 0.2 }}
                              >
                                <Box
                                  sx={{
                                    textAlign: "left",
                                    display: "flex",
                                    alignItems: "baseline",
                                    flexDirection: "column",
                                  }}
                                >
                                  <Box
                                    sx={{
                                      fontWeight: "800",
                                      fontSize: { xs: "24px", md: "28px" },
                                      fontFamily: "RobotoSlabBold",
                                      background: "linear-gradient(135deg, #a4243d 0%, #ff6b6b 100%)",
                                      WebkitBackgroundClip: "text",
                                      WebkitTextFillColor: "transparent",
                                      backgroundClip: "text",
                                      lineHeight: 1.2,
                                    }}
                                  >
                                    ₹{firstVariant?.price || product.livePrice || product.price}
                                  </Box>
                                </Box>
                              </motion.div>
                            )}
                          </Box>

                          <Box 
                            sx={{ 
                              flexShrink: 0,
                              display: "flex",
                              alignItems: "flex-end",
                            }}
                          >
                            {product.status === "active" ? (
                              <motion.div
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                              >
                                <Button
                                  variant="contained"
                                  sx={{
                                    background: "linear-gradient(135deg, #a4243d 0%, #ff6b6b 100%)",
                                    color: "white",
                                    fontWeight: "700",
                                    fontSize: "13px",
                                    padding: { xs: "8px 16px", md: "10px 20px" },
                                    borderRadius: "12px",
                                    textTransform: "none",
                                    fontFamily: "RobotoSlab",
                                    boxShadow: "0 4px 15px rgba(164, 36, 61, 0.3)",
                                    transition: "all 0.3s ease",
                                    whiteSpace: "nowrap",
                                    minWidth: "80px",
                                    maxWidth: "100%",
                                    "&:hover": {
                                      background: "linear-gradient(135deg, #8a1e33 0%, #e55555 100%)",
                                      boxShadow: "0 6px 20px rgba(164, 36, 61, 0.4)",
                                      transform: "translateY(-2px)",
                                    },
                                  }}
                                  onClick={() => {
                                    navigate(`/pilot/products/${product.id}`);
                                  }}
                                >
                                  View
                                </Button>
                              </motion.div>
                            ) : (
                              <Button
                                variant="outlined"
                                size="small"
                                disabled
                                sx={{
                                  opacity: "0.5",
                                  borderRadius: "12px",
                                  padding: { xs: "8px 16px", md: "10px 20px" },
                                  borderColor: "#dee2e6",
                                  color: "#6c757d",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                Unavailable
                              </Button>
                            )}
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  </motion.div>
                </Grid>
                    );
                  })}
                  </Grid>
                </Box>
              </motion.div>
            ))}
          </Box>
          )}
        </Box>
      )}
      <PilotCartFAB />
    </Box>
  );
}

export default Products;
